import { ISessionData, ISessionVehicle, IVehicle, IVehicleId, sessionVehicleSchema, vehicleSchema } from 'data/schema';
import { userSession } from 'state/session';
import { isObject } from 'utils/objects';
import { showConfirmDialog } from 'state/confirm-dialog';
import { t } from '@lingui/core/macro';
import { tenantConfig } from 'utils/tenant';
import { vehiclesMatch } from 'utils/vehicles';
import { getComparisonVehicle } from './compare';
import { showToast } from 'state/toast';
import logger from 'utils/log';

/**
 * This vehicle schema is used when resetting session data from vehicle registry
 * Vehicles in the registry may not have all the fields present in the session
 * such as dgaLink or vinOrMarketingCode, which may be undefined
 * This schema transformation ensures that missing fields are explicitly set to undefined
 */
const vehicleSchemaWithUndefined = sessionVehicleSchema.transform((vehicle) => {
  const result = { ...vehicle } as Record<string, unknown>;

  // Iterate through schema shape to find optional fields that are missing
  const schemaShape = sessionVehicleSchema.shape as Record<keyof ISessionVehicle, unknown>;

  for (const key of Object.keys(schemaShape)) {
    // Set undefined for any field that doesn't exist in the vehicle data
    if (!(key in vehicle)) {
      result[key] = undefined;
    }
  }

  return result as ISessionVehicle;
});

/**
 * Helper function to determine the next comparison mode when removing vehicles
 */
function getNextComparisonMode(vehiclesCount: number, removedActiveOrStashed: boolean): ISessionData['comparisonMode'] {
  if (vehiclesCount === 0) return 'off';
  const currentMode = userSession().get('comparisonMode');
  if (removedActiveOrStashed && currentMode !== 'off') return 'selecting';
  return currentMode;
}

/**
 * Helper function to reset all active vehicle fields
 */
function resetActiveVehicle() {
  userSession().setValues({
    ...vehicleSchemaWithUndefined.parse({}),
    activeVehicleId: undefined,
  });
}

/**
 * Add vehicle to the registry. Optionally (and by default) activate it immediately
 *
 * @param vehicle - Partial vehicle data to add
 * @param makeActive - Whether to activate the vehicle after adding (default: true)
 * @returns Vehicle ID (either newly created or existing if duplicate found)
 * @throws {Error} If vehicle data is invalid and cannot be parsed
 */
export function addVehicle(vehicle: Partial<IVehicle>, makeActive: boolean = true): IVehicleId {
  let newVehicle: IVehicle;

  try {
    newVehicle = vehicleSchema.parse(vehicle);
  } catch (error) {
    logger.error('[VehicleRegistry] Failed to parse vehicle data:', error);
    showToast({
      title: t`Failed to add vehicle`,
      message: t`The vehicle data is invalid.`,
      type: 'error',
      notificationId: 'vehicle-add-error',
    });
    throw error;
  }

  // Check if a vehicle with the same prString, vinOrMarketingCode, or okapiCode already exists
  const existingId = (['prString', 'vinOrMarketingCode', 'okapiCode'] as const).reduce<IVehicleId | undefined>(
    (found, key) => found || (newVehicle[key] && findVehicleId(key, newVehicle[key])),
    undefined
  );

  if (existingId) {
    if (makeActive) {
      activateVehicle(existingId);
    }
    return existingId; // Return early without adding the duplicate vehicle
  }

  // add new vehicle
  const newId = crypto.randomUUID();
  const updatedVehicles = { ...userSession().get('vehicles'), [newId]: newVehicle };

  userSession().setValues(
    { vehicles: updatedVehicles },
    { emit: !makeActive } // do not emit if we also need to activate the vehicle
  );

  if (makeActive) {
    activateVehicle(newId);
  }

  return newId;
}

/**
 * Set a vehicle active using the ID of an existing vehicle in the vehicles registry.
 * When a vehicle is active, it's going to be the one that's used in the stream
 *
 * @param id - The vehicle ID to activate
 * @returns true if activation succeeded, false otherwise
 */
export function activateVehicle(id: IVehicleId): boolean {
  const vehicles = userSession().get('vehicles');

  // Check if the vehicle exists in the registry. Also do not activate if already comparing
  if (!vehicles || !vehicles[id] || userSession().get('comparisonMode') === 'on') {
    return false;
  }

  const vehicleData = vehicleSchemaWithUndefined.parse(vehicles[id]);

  // Apply the vehicle's data to the session root level
  userSession().setValues({
    ...vehicleData,
    activeVehicleId: id,
    streamActive: true,
    endedAt: undefined,
  });

  return true;
}

/**
 * Swaps the active vehicle with a comparing vehicle.
 *
 * Behavior:
 * - Stashes the currently active vehicle (sets comparing: true)
 * - Activates the target vehicle (sets as activeVehicleId)
 * - Only works when NOT in 'on' comparison mode (enforced by activateVehicle)
 *
 * @param nextVehicleId - ID of the vehicle to activate
 *
 * @example
 * // Swap from vehicle1 to vehicle2
 * swapComparedVehicles(vehicle2Id);
 */
export function swapComparedVehicles(nextVehicleId: IVehicleId) {
  const session = userSession().get();
  const currentActiveVehicleId = session.activeVehicleId;

  if (!currentActiveVehicleId || !nextVehicleId) {
    return;
  }

  const currentActiveVehicle = userSession().currentVehicle;

  if (!currentActiveVehicle) {
    return;
  }

  const nextVehicle = session.vehicles?.[nextVehicleId];

  if (!nextVehicle) {
    return;
  }

  stashVehicle(currentActiveVehicleId, false);

  activateVehicle(nextVehicleId);
}

/**
 * Marks a vehicle for comparison by setting its comparing flag to true.
 * Automatically disables the comparing flag on all other vehicles.
 *
 * @param targetVehicleId - ID of the vehicle to mark for comparison
 * @param emit - Whether to emit state change event (default: true)
 *
 * @example
 * // Stash vehicle2 for comparison
 * stashVehicle(vehicle2Id);
 */
export function stashVehicle(targetVehicleId: IVehicleId, emit: boolean = true) {
  const vehicles = userSession().get('vehicles');
  if (!vehicles || !targetVehicleId || !vehicles[targetVehicleId]) {
    return;
  }
  const myVehicle = vehicles[targetVehicleId];
  // Set comparing flag on the active vehicle - stash it
  const updatedVehicles = {
    ...disableComparingForVehicles(false),
    [targetVehicleId]: {
      ...myVehicle,
      comparing: true,
    },
  };

  userSession().setValues({ vehicles: updatedVehicles }, { emit });
}

/**
 * Sets comparing: false on all vehicles and returns the updated vehicles object.
 * @returns The updated vehicles registry with comparing set to false for all vehicles, or an empty object if no vehicles exist.
 */
export function disableComparingForVehicles(emit: boolean = true): Record<string, IVehicle> {
  const vehicles = userSession().get('vehicles');
  // Set comparing: false on all vehicles
  if (!vehicles) {
    return {};
  }
  const updatedVehicles: Record<string, IVehicle> = {};
  for (const vehicleId of Object.keys(vehicles || {})) {
    updatedVehicles[vehicleId] = {
      ...vehicles[vehicleId],
      comparing: false,
    };
  }
  userSession().setValues({ vehicles: updatedVehicles }, { emit });
  return updatedVehicles;
}

/**
 * Remove vehicle from the comparison candidates
 */
export async function deactivateVehicleComparison(deactivatedVehicleId: IVehicleId) {
  const vehicles = userSession().get('vehicles');
  const stashedVehicle = vehicles ? vehicles[deactivatedVehicleId] : undefined;

  if (!vehicles || !stashedVehicle) {
    return;
  }

  const result = await showConfirmDialog({
    title: t`Discard vehicle from compare session`,
    message: `Are you sure you want to discard ${stashedVehicle.carDisplayName}  from your compare session?`,
    confirmText: t`Discard vehicle`,
    cancelText: t`Keep both vehicles`,
    'data-testid': 'deactivate-vehicle-comparison-dialog',
  });

  if (!result) {
    // user cancelled the action
    return;
  }

  // 1. The vehicle we are deactivating needs to be stashed and no longer active
  // 2. If there is another vehicle set for comparison, that vehicle becomes active

  const comparisonVehicleIds = getComparingVehicleIds();
  const remainingVehicleId = comparisonVehicleIds.find((id) => id && id !== deactivatedVehicleId);

  if (remainingVehicleId) {
    stashVehicle(remainingVehicleId, false);
  }

  // If we are removing active or stashed vehicle and comparison mode = on then comparison mode is set to 'selecting'
  // However, if we are removing the last car in the registry, comparison mode is set to 'off'
  const lastComparisonMode = userSession().get('comparisonMode');
  const nextComparisonMode: ISessionData['comparisonMode'] =
    lastComparisonMode === 'on' && remainingVehicleId ? 'selecting' : 'off';

  // Try not to emit if we know activation will follow
  userSession().setValues({ comparisonMode: nextComparisonMode }, { emit: false });

  if (remainingVehicleId) {
    activateVehicle(remainingVehicleId);
  } else {
    // No vehicles left, reset all vehicle fields
    resetActiveVehicle();
  }
}

// get the vehicles we have in comparison (active + stashed)
export function getComparingVehicleIds() {
  const [stashedVehicleId] = getComparisonVehicle();
  const activeVehicleId = userSession().get('activeVehicleId');

  const comparisonVehicleIds = [activeVehicleId, stashedVehicleId].filter(Boolean);
  return [...new Set(comparisonVehicleIds)];
}

export function getComparingVehicles() {
  const ids = getComparingVehicleIds();
  const vehicles = userSession().get('vehicles');

  return ids.reduce(
    (acc, id) => {
      if (id && vehicles?.[id]) {
        acc[id] = vehicles[id];
      }
      return acc;
    },
    {} as Record<IVehicleId, IVehicle>
  );
}

export function isVehicleStashed(vehicle?: IVehicle): boolean {
  const comparisonVehicle = getComparisonVehicle();
  const stashed = comparisonVehicle ? comparisonVehicle[1] : undefined;

  if (!stashed || !vehicle || !isObject(vehicle) || !isObject(stashed)) return false;

  return vehiclesMatch(stashed, vehicle);
}

/**
 * Checks if the stashed comparison vehicle and the active vehicle are the same.
 * Vehicles are considered the same if prString, vinOrMarketingCode, or okapiCode match.
 *
 * @returns true if the vehicles match, false otherwise
 *
 * @example
 * if (areComparedVehiclesEqual()) {
 *   showToast({ message: 'Cannot compare a vehicle with itself' });
 * }
 */
export function areComparedVehiclesEqual(): boolean {
  const vehicles = userSession().get('vehicles');
  const activeVehicleId = userSession().get('activeVehicleId');
  const activeVehicle = activeVehicleId ? vehicles?.[activeVehicleId] : undefined;
  const [, stashedVehicle] = getComparisonVehicle();

  if (!activeVehicle || !stashedVehicle) return false;

  return vehiclesMatch(activeVehicle, stashedVehicle);
}

/**
 * Find vehicle by any property and return its id in the vehicles registry
 */
export function findVehicleId<K extends keyof IVehicle>(key: K, value: IVehicle[K]): IVehicleId | undefined {
  const vehicles = userSession().get('vehicles');

  if (!vehicles) {
    return undefined;
  }

  // Find the first vehicle that matches the key-value pair
  for (const [vehicleId, vehicleData] of Object.entries(vehicles)) {
    if ((vehicleData as IVehicle)[key] === value) {
      return vehicleId;
    }
  }

  return undefined;
}

/**
 * Remove a vehicle from the registry by its ID
 */
export function removeVehicle(removalVehicleId: IVehicleId): boolean {
  const vehicles = userSession().get('vehicles');

  // Check if the vehicle exists in the registry
  if (!vehicles || !vehicles[removalVehicleId]) {
    return false;
  }

  const wasActiveVehicle = userSession().get('activeVehicleId') === removalVehicleId;

  // Create a new vehicles object without the removed vehicle
  const updatedVehicles = Object.entries(vehicles).reduce(
    (acc, [vehicleId, vehicleData]) => {
      if (vehicleId !== removalVehicleId) {
        acc[vehicleId] = vehicleData as IVehicle;
      }
      return acc;
    },
    {} as Record<string, IVehicle>
  );

  const [stashedVehicleId] = getComparisonVehicle();

  // Determine next comparison mode
  const removedActiveOrStashed = wasActiveVehicle || stashedVehicleId === removalVehicleId;
  const nextComparisonMode = getNextComparisonMode(Object.keys(updatedVehicles).length, removedActiveOrStashed);

  // Update the vehicles registry
  userSession().setValues({ vehicles: updatedVehicles, comparisonMode: nextComparisonMode });

  // If we removed the active vehicle, try to activate the most recent one
  if (wasActiveVehicle) {
    const recentVehicles = getRecentVehicles(1);
    if (recentVehicles.length > 0) {
      activateVehicle(recentVehicles[0].id);
    } else {
      // No vehicles left, reset all vehicle fields
      resetActiveVehicle();
    }
  }

  return true;
}

/**
 * Get the most recently added vehicles from the registry
 * @param session - The session state instance
 * @param count - Number of vehicles to return (default: 2)
 * @returns Array of vehicles with their IDs, sorted by addedAt timestamp (most recent first)
 */
export function getRecentVehicles(count: number = 2): Array<{ id: IVehicleId; vehicle: IVehicle }> {
  const vehicles = userSession().get('vehicles');

  if (!vehicles || Object.keys(vehicles).length === 0) {
    return [];
  }

  // Convert to array, sort by addedAt descending, and take the first 'count' items
  return Object.entries(vehicles)
    .map(([id, vehicleData]) => ({ id, vehicle: vehicleData as IVehicle }))
    .sort((a, b) => b.vehicle.addedAt - a.vehicle.addedAt)
    .slice(0, count);
}

/**
 * Vehicle related updates that will update the active car and the record in the registry
 */
export function updateVehicle(
  updates: Partial<IVehicle>,
  id: IVehicleId | undefined = userSession().get('activeVehicleId')
): void {
  const vehicles = userSession().get('vehicles');
  const current = userSession().currentVehicle;

  if (!vehicles || !id || !vehicles[id]) {
    return;
  }

  const parsedUpdates = vehicleSchema.parse({ ...current, ...updates });

  const activeUpdates = id === userSession().get('activeVehicleId') ? parsedUpdates : {};
  userSession().setValues({
    vehicles: {
      ...vehicles,
      [id]: parsedUpdates,
    },
    ...activeUpdates,
  });
}

export function setComparingMode(mode: ISessionData['comparisonMode']) {
  const session = userSession().get();
  const activeVehicleId = session.activeVehicleId;
  if (!activeVehicleId) {
    return;
  }

  userSession().setValues({
    comparisonMode: mode,
  });
}

/**
 * Comparison mode transition map
 */
const COMPARISON_TRANSITIONS: Record<ISessionData['comparisonMode'], ISessionData['comparisonMode']> = {
  off: 'selecting',
  selecting: 'on',
  on: 'off',
};

export async function toggleComparingMode() {
  if (!tenantConfig().isAllowed('comparison')) {
    return;
  }
  const currentMode = userSession().get('comparisonMode');
  setComparingMode(COMPARISON_TRANSITIONS[currentMode]);
  return userSession().get('comparisonMode');
}
